import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { forwardRef, useImperativeHandle } from 'react';

import { useScroll } from './use-scroll';

const scrollbarSizes = {
  sm: 6,
  md: 8,
  lg: 12,
};

export interface ScrollAreaProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactNode;
  /** Scrollbar orientation - which scrollbars to show */
  orientation?: 'vertical' | 'horizontal' | 'both';
  /** Auto-hide scrollbars when not scrolling */
  autoHide?: boolean;
  /** Delay before hiding scrollbars (ms) */
  autoHideDelay?: number;
  /** Custom class for the scrollbar track */
  trackClassName?: string;
  /** Custom class for the scrollbar thumb */
  thumbClassName?: string;
  /** Custom class for the vertical scrollbar container */
  verticalClassName?: string;
  /** Custom class for the horizontal scrollbar container */
  horizontalClassName?: string;
  /** Custom styles for scrollbar track */
  trackStyle?: CSSProperties;
  /** Custom styles for scrollbar thumb */
  thumbStyle?: CSSProperties;
  /** Scrollbar size (width for vertical, height for horizontal) */
  scrollbarSize?: 'sm' | 'md' | 'lg' | number;
  /** Minimum thumb size in pixels */
  minThumbSize?: number;
  /** Add padding to content to prevent it from being hidden behind scrollbar */
  scrollbarPadding?: boolean;
  /** Called when scroll position changes */
  onScrollChange?: (position: { x: number; y: number }) => void;
  /** Maximum height for the scroll area - enables grow-to-fit behavior */
  maxHeight?: number | string;
}

export interface ScrollAreaRef {
  /** Scroll to a specific position */
  scrollTo: (options: { x?: number; y?: number }) => void;
  /** Get the viewport element */
  getViewport: () => HTMLDivElement | null;
}

/**
 * ScrollArea component with custom scrollbars
 *
 * @example
 * ```tsx
 * <ScrollArea className="h-64">
 *   <div className="p-4">
 *     Long content here...
 *   </div>
 * </ScrollArea>
 * ```
 *
 * @example
 * ```tsx
 * // With auto-hide and custom styling
 * <ScrollArea
 *   autoHide
 *   autoHideDelay={500}
 *   thumbClassName="bg-blue-500"
 *   scrollbarSize="lg"
 * >
 *   Content...
 * </ScrollArea>
 * ```
 */
export const ScrollArea = forwardRef<ScrollAreaRef, ScrollAreaProps>(function ScrollArea(
  {
    children,
    className = '',
    orientation = 'both',
    autoHide = false,
    autoHideDelay = 1000,
    trackClassName = '',
    thumbClassName = '',
    verticalClassName = '',
    horizontalClassName = '',
    trackStyle,
    thumbStyle,
    scrollbarSize = 'md',
    minThumbSize = 30,
    scrollbarPadding = false,
    onScrollChange,
    maxHeight,
    ...props
  },
  ref
) {
  const {
    viewportRef,
    contentRef,
    scrollState,
    isVisible,
    isDragging,
    verticalHandlers,
    horizontalHandlers,
    containerHandlers,
    scrollTo,
  } = useScroll({
    autoHide,
    autoHideDelay,
    minThumbSize,
    onScrollChange,
  });

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    scrollTo,
    getViewport: () => viewportRef.current,
  }));

  const size = typeof scrollbarSize === 'number' ? scrollbarSize : scrollbarSizes[scrollbarSize];

  const showVertical =
    (orientation === 'vertical' || orientation === 'both') && scrollState.hasVerticalScroll;
  const showHorizontal =
    (orientation === 'horizontal' || orientation === 'both') && scrollState.hasHorizontalScroll;

  // Calculate padding for content if scrollbarPadding is enabled
  const contentPadding: CSSProperties = scrollbarPadding
    ? {
        paddingRight: showVertical ? size + 4 : undefined,
        paddingBottom: showHorizontal ? size + 4 : undefined,
      }
    : {};

  // Determine visibility based on autoHide setting
  const scrollbarVisible = autoHide ? isVisible || isDragging : true;

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={containerHandlers.onMouseEnter}
      onMouseLeave={containerHandlers.onMouseLeave}
      onMouseMove={containerHandlers.onMouseMove}
      {...props}
    >
      {/* Viewport - scrollable container with hidden native scrollbars */}
      <div
        ref={viewportRef}
        className={maxHeight ? 'w-full overflow-auto' : 'h-full w-full overflow-auto'}
        style={{
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          ...(maxHeight ? { maxHeight } : {}),
        }}
      >
        {/* Content wrapper */}
        <div ref={contentRef} style={contentPadding}>
          {children}
        </div>

        {/* Hide webkit scrollbar */}
        <style>{`
					[data-scroll-area-viewport]::-webkit-scrollbar {
						display: none;
					}
				`}</style>
      </div>

      {/* Vertical Scrollbar */}
      {showVertical ? (
        <div
          className={`absolute z-50 transition-opacity duration-200 ${verticalClassName}`}
          style={{
            width: size,
            top: 2,
            right: 2,
            bottom: showHorizontal ? size + 4 : 2,
            opacity: scrollbarVisible ? 1 : 0,
            pointerEvents: scrollbarVisible ? 'auto' : 'none',
          }}
        >
          {/* Track */}
          <div
            role="scrollbar"
            aria-controls="scroll-viewport"
            aria-valuenow={Math.round(scrollState.verticalThumbTop)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-orientation="vertical"
            tabIndex={-1}
            className={`h-full w-full rounded-full bg-gray-200 ${trackClassName}`}
            style={trackStyle}
            onClick={verticalHandlers.onTrackClick}
            onKeyDown={(e) => e.preventDefault()}
          >
            {/* Thumb */}
            <div
              role="slider"
              tabIndex={0}
              aria-label="Vertical scroll thumb"
              aria-valuenow={Math.round(scrollState.verticalThumbTop)}
              aria-valuemin={0}
              aria-valuemax={100}
              className={`absolute w-full rounded-full bg-gray-400 hover:bg-gray-500 active:bg-gray-600 cursor-pointer transition-colors flex items-center justify-center ${thumbClassName}`}
              style={{
                height: `${scrollState.verticalThumbHeight}%`,
                top: `${scrollState.verticalThumbTop}%`,
                ...thumbStyle,
              }}
              onMouseDown={verticalHandlers.onThumbMouseDown}
              onTouchStart={verticalHandlers.onThumbTouchStart}
              onKeyDown={(e) => e.preventDefault()}
            >
              {/* Grip lines */}
              <div className="flex flex-col gap-pb-0.5 py-px">
                <div className="w-pb-1.5 h-px bg-gray-600 rounded-full opacity-50" />
                <div className="w-pb-1.5 h-px bg-gray-600 rounded-full opacity-50" />
                <div className="w-pb-1.5 h-px bg-gray-600 rounded-full opacity-50" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Horizontal Scrollbar */}
      {showHorizontal ? (
        <div
          className={`absolute z-50 transition-opacity duration-200 ${horizontalClassName}`}
          style={{
            height: size,
            left: 2,
            right: showVertical ? size + 4 : 2,
            bottom: 2,
            opacity: scrollbarVisible ? 1 : 0,
            pointerEvents: scrollbarVisible ? 'auto' : 'none',
          }}
        >
          {/* Track */}
          <div
            role="scrollbar"
            aria-controls="scroll-viewport"
            aria-valuenow={Math.round(scrollState.horizontalThumbLeft)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-orientation="horizontal"
            tabIndex={-1}
            className={`h-full w-full rounded-full bg-gray-200 ${trackClassName}`}
            style={trackStyle}
            onClick={horizontalHandlers.onTrackClick}
            onKeyDown={(e) => e.preventDefault()}
          >
            {/* Thumb */}
            <div
              role="slider"
              tabIndex={0}
              aria-label="Horizontal scroll thumb"
              aria-valuenow={Math.round(scrollState.horizontalThumbLeft)}
              aria-valuemin={0}
              aria-valuemax={100}
              className={`absolute h-full rounded-full bg-gray-400 hover:bg-gray-500 active:bg-gray-600 cursor-pointer transition-colors flex items-center justify-center ${thumbClassName}`}
              style={{
                width: `${scrollState.horizontalThumbWidth}%`,
                left: `${scrollState.horizontalThumbLeft}%`,
                ...thumbStyle,
              }}
              onMouseDown={horizontalHandlers.onThumbMouseDown}
              onTouchStart={horizontalHandlers.onThumbTouchStart}
              onKeyDown={(e) => e.preventDefault()}
            >
              {/* Grip lines */}
              <div className="flex flex-row gap-pb-0.5 px-px">
                <div className="h-pb-1.5 w-px bg-gray-600 rounded-full opacity-50" />
                <div className="h-pb-1.5 w-px bg-gray-600 rounded-full opacity-50" />
                <div className="h-pb-1.5 w-px bg-gray-600 rounded-full opacity-50" />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});
